import type { ElementType } from "react";
import {
  IconAlertTriangle,
  IconBellRinging,
  IconBuildingBank,
  IconCalculator,
  IconChartBar,
  IconClockHour4,
  IconFileCertificate,
  IconGavel,
  IconHeadset,
  IconReceipt,
  IconScale,
  IconSend,
  IconShieldCheck,
  IconTrendingUp,
  IconUsers,
} from "@tabler/icons-react";

export interface MotivationBenefit {
  title: string;
  text: string;
  icon: ElementType;
}

export interface Motivation {
  value: string;
  label: string;
  description: string;
  icon: ElementType;
  heroTitle: string;
  heroSubtitle: string;
  benefits: MotivationBenefit[];
}

export const MOTIVATION_QUESTION = "Qu'attendez-vous de Qalimo en priorité ?";

export const MOTIVATIONS: Motivation[] = [
  {
    value: "TEMPS",
    label: "Gagner du temps",
    description: "Quittances, relances et suivi des loyers en automatique",
    icon: IconClockHour4,
    heroTitle: "Récupérez des heures chaque mois",
    heroSubtitle:
      "Quittances envoyées, loyers pointés, locataires relancés : Qalimo s'en occupe automatiquement, vous gardez la main.",
    benefits: [
      {
        title: "Quittancement automatique",
        text: "Vos quittances sont générées et envoyées chaque mois, sans y penser.",
        icon: IconReceipt,
      },
      {
        title: "Relances automatiques",
        text: "Rappels d'échéance et relances de retard partent tout seuls, au bon moment.",
        icon: IconBellRinging,
      },
      {
        title: "Synchronisation bancaire",
        text: "Les loyers reçus sont rapprochés automatiquement de vos locataires.",
        icon: IconBuildingBank,
      },
    ],
  },
  {
    value: "JURIDIQUE",
    label: "Être en règle",
    description:
      "Baux conformes, indexations et obligations légales maîtrisées",
    icon: IconScale,
    heroTitle: "Louez en règle, sans être juriste",
    heroSubtitle:
      "Bail rédigé par un huissier de justice, indexations et régularisations aux bonnes dates, assistance juridique illimitée.",
    benefits: [
      {
        title: "Bail rédigé par un huissier",
        text: "Des contrats conformes et à jour des dernières lois, pour chaque type de location.",
        icon: IconFileCertificate,
      },
      {
        title: "Indexation annuelle du loyer",
        text: "La révision IRL est calculée et appliquée au bon moment, sans erreur.",
        icon: IconTrendingUp,
      },
      {
        title: "Assistance juridique illimitée",
        text: "Une question ? Notre équipe vous répond, autant de fois que nécessaire.",
        icon: IconHeadset,
      },
    ],
  },
  {
    value: "IMPAYES",
    label: "Sécuriser mes loyers",
    description: "Détecter les retards et gérer les impayés sans stress",
    icon: IconShieldCheck,
    heroTitle: "Un loyer en retard ne passe plus inaperçu",
    heroSubtitle:
      "Votre banque synchronisée, les retards détectés immédiatement, des relances graduées et un dossier complet si la situation se durcit.",
    benefits: [
      {
        title: "Détection immédiate des retards",
        text: "La synchronisation bancaire repère les loyers manquants dès l'échéance.",
        icon: IconAlertTriangle,
      },
      {
        title: "Relances graduées",
        text: "Du simple rappel à la mise en demeure, chaque étape est prête à partir.",
        icon: IconSend,
      },
      {
        title: "Assistance en cas de contentieux",
        text: "Un impayé qui s'installe ? Vous êtes accompagné, avec un dossier complet.",
        icon: IconGavel,
      },
    ],
  },
  {
    value: "PILOTAGE",
    label: "Piloter mes finances",
    description: "Rentabilité, comptabilité et fiscalité en un coup d'œil",
    icon: IconChartBar,
    heroTitle: "Votre patrimoine locatif, piloté comme un pro",
    heroSubtitle:
      "Rentabilité par bien, régularisations de charges et documents comptables prêts pour votre déclaration.",
    benefits: [
      {
        title: "Suivi financier par bien",
        text: "Loyers, charges et rentabilité de chaque bien, toujours à jour.",
        icon: IconChartBar,
      },
      {
        title: "Régularisation des charges",
        text: "Le calcul annuel des charges locataires se fait en quelques clics.",
        icon: IconCalculator,
      },
      {
        title: "Multi-bailleurs",
        text: "SCI, LMNP, nom propre : tout votre patrimoine au même endroit.",
        icon: IconUsers,
      },
    ],
  },
];

export function getMotivation(value: string | null | undefined) {
  return MOTIVATIONS.find((motivation) => motivation.value === value);
}
